import * as XLSX from 'xlsx';

/**
 * Normaliza texto eliminando acentos y espacios extra
 */
export const normalize = (t) => (t || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

/**
 * Obtiene un ID de pedido de forma segura
 */
export const getPedidoId = (p) => String((p && (p.id_pedido ?? p.id ?? p._id)) || '');

/**
 * Calcula el rango de fechas de un set de pedidos
 */
export const calculateDateRange = (data) => {
    if (!data || data.length === 0) return { min: '', max: '' };

    const dates = data.map((pedido) => {
        const parts = pedido.fecha.split('-');
        return new Date(parts[0], parts[1] - 1, parts[2]);
    });

    dates.sort((a, b) => a - b);
    return {
        min: dates[0].toLocaleDateString(),
        max: dates[dates.length - 1].toLocaleDateString()
    };
};

/**
 * Exporta pedidos a un archivo Excel (.xlsx)
 */
export const exportPedidosToExcel = (pedidos, bebidas, minDate, maxDate) => {
    // Mapa de bebidas
    const bebidaMap = {};
    bebidas.forEach(b => {
        bebidaMap[b.id_bebida] = `${b.marca} - ${b.sabor}`;
    });

    const sanitize = (pedido) => {
        const bebidaNombre =
            pedido?.bebida?.marca && pedido?.bebida?.sabor
                ? `${pedido.bebida.marca} - ${pedido.bebida.sabor}`
                : (pedido?.bebida_id && bebidaMap[pedido.bebida_id] ? bebidaMap[pedido.bebida_id] : '');

        return {
            id_pedido: pedido.id_pedido ?? '',
            fecha: pedido.fecha ?? '',
            comida: pedido.comida ?? pedido.tipo ?? '',
            usuario: pedido.usuarioInfo?.usuario || pedido.usuario || '',
            nombre: pedido.usuarioInfo?.nombreuser || pedido.nombreuser || '',
            apellido: pedido.usuarioInfo?.apellido || pedido.apellido || '',
            sector: pedido.sectorInfo?.sector || pedido.usuarioInfo?.sectorinfo?.sector || pedido.sector || '',
            sede: pedido.sede?.nombre_sede || pedido.sede?.direccion || pedido.ubicacion || '',
            plato: pedido.plato?.nombre || pedido.nombre || '',
            bebida: bebidaNombre,
            observacion: pedido.observacion || ''
        };
    };

    // Separar almuerzo y cena
    let pedidosAlmuerzo = pedidos.filter(p => normalize(p.comida) === 'almuerzo');
    let pedidosCena = pedidos.filter(p => normalize(p.comida) === 'cena');

    if (pedidos.length > 0 && pedidosAlmuerzo.length === 0 && pedidosCena.length === 0) {
        pedidosAlmuerzo = pedidos.filter(p => normalize(p.tipo) === 'almuerzo');
        pedidosCena = pedidos.filter(p => normalize(p.tipo) === 'cena');
    }

    const worksheetAlmuerzo = XLSX.utils.json_to_sheet(pedidosAlmuerzo.map(sanitize));
    const worksheetCena = XLSX.utils.json_to_sheet(pedidosCena.map(sanitize));

    const countsAlmuerzoByFecha = {};
    const countsCenaByFecha = {};
    let totalAlmuerzo = 0;
    let totalCena = 0;

    pedidosAlmuerzo.forEach((p) => {
        countsAlmuerzoByFecha[p.fecha] = (countsAlmuerzoByFecha[p.fecha] || 0) + 1;
        totalAlmuerzo += 1;
    });
    pedidosCena.forEach((p) => {
        countsCenaByFecha[p.fecha] = (countsCenaByFecha[p.fecha] || 0) + 1;
        totalCena += 1;
    });

    const countpage = [['Almuerzo'], ['Fecha', 'Cantidad']];
    Object.keys(countsAlmuerzoByFecha).sort().forEach((f) => countpage.push([f, countsAlmuerzoByFecha[f]]));
    countpage.push(['Subtotal Almuerzo', totalAlmuerzo], [], ['Cena'], ['Fecha', 'Cantidad']);
    Object.keys(countsCenaByFecha).sort().forEach((f) => countpage.push([f, countsCenaByFecha[f]]));
    countpage.push(['Subtotal Cena', totalCena], [], ['Totales Generales'], ['Almuerzo', totalAlmuerzo], ['Cena', totalCena], ['Total', totalAlmuerzo + totalCena]);

    const countSheet = XLSX.utils.aoa_to_sheet(countpage);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheetAlmuerzo, 'Pedidos_Almuerzo');
    XLSX.utils.book_append_sheet(workbook, worksheetCena, 'Pedidos_Cena');
    XLSX.utils.book_append_sheet(workbook, countSheet, 'ConteoPedidos');

    XLSX.writeFile(workbook, `pedidos_semana_${minDate || 'ini'}_al_${maxDate || 'fin'}.xlsx`);
};

/**
 * Genera el ZPL para una etiqueta individual basado en la configuración
 */
export const generateZPLFromConfig = (pedido, config) => {
    const LABEL_WIDTH = 100;
    const DPI = 203;
    const MM_TO_DOTS = DPI / 25.4;
    const dotsWidth = Math.round(LABEL_WIDTH * MM_TO_DOTS);
    const dotsHeight = Math.round(50 * MM_TO_DOTS);

    let zpl = `^XA\n^PW${dotsWidth}\n^LL${dotsHeight}\n^CI28\n`;
    const fechaVencimiento = new Date(new Date(pedido.fecha).getTime() + 7 * 24 * 60 * 60 * 1000);

    if (config && config.campos) {
        config.campos.filter(c => c.visible).forEach(campo => {
            const xDots = Math.round((campo.x || 0) * MM_TO_DOTS);
            const yDots = Math.round((campo.y || 0) * MM_TO_DOTS);
            const fontHeight = Math.round((campo.fontSize || 16) * 1.2);
            const orient = { 0: 'N', 90: 'R', 180: 'I', 270: 'B' }[campo.rotation || 0] || 'N';

            if (campo.id === 'separador') {
                zpl += `^FO${xDots},${yDots}^GB${Math.round(80 * MM_TO_DOTS)},3,3^FS\n`;
                return;
            }

            if (campo.id === 'qrCode') {
                const mag = Math.max(2, Math.min(10, Math.round((campo.qrSize || 30) / 4)));
                const nombreUsuario = pedido.usuarioInfo?.nombreuser || pedido.nombreuser || '';
                const nombrePlato = pedido.plato?.nombre || pedido.nombre || '';
                const qrData = `${getPedidoId(pedido)}|${nombreUsuario}|${nombrePlato.slice(0, 60)}`;
                zpl += `^FO${xDots},${yDots}^BQN,2,${mag}^FDLA,${qrData}^FS\n`;
                return;
            }

            if (campo.id === 'barcode') {
                const barH = Math.max(20, Math.round((campo.barcodeHeight || 50) * MM_TO_DOTS));
                zpl += `^FO${xDots},${yDots}^BC${orient},${barH},Y,N,N^FD${getPedidoId(pedido)}^FS\n`;
                return;
            }

            let texto = '';
            switch (campo.id) {
                case 'header': texto = 'CIASA'; break;
                case 'nombre':
                    const nombre = pedido.usuarioInfo?.nombreuser || pedido.nombreuser || '';
                    const apellido = pedido.usuarioInfo?.apellido || pedido.apellido || '';
                    texto = `${campo.prefijo || ''}${nombre} ${apellido}`.trim();
                    break;
                case 'apellido': texto = `${campo.prefijo || ''}${pedido.usuarioInfo?.apellido || pedido.apellido || ''}`; break;
                case 'plato': texto = `${campo.prefijo || ''}${pedido.plato?.nombre || pedido.nombre || ''}`; break;
                case 'sector': texto = `${campo.prefijo || ''}${pedido.sectorInfo?.sector || pedido.usuarioInfo?.sectorinfo?.sector || pedido.sector || ''}`; break;
                case 'ubicacion': texto = `${campo.prefijo || ''}${pedido.sede?.nombre_sede || pedido.sede?.direccion || pedido.ubicacion || ''}`; break;
                case 'sede': texto = `${campo.prefijo || ''}${pedido.sede?.nombre_sede || pedido.sede?.direccion || pedido.ubicacion || ''}`; break;
                case 'fechaVencimiento': texto = `${campo.prefijo || ''}${fechaVencimiento.toLocaleDateString('es-AR')}`; break;
                default: texto = campo.label || '';
            }
            zpl += `^FO${xDots},${yDots}^A0${orient},${fontHeight},${fontHeight}^FD${texto}^FS\n`;
        });
    } else {
        const nombre = pedido.usuarioInfo?.nombreuser || pedido.nombreuser || '';
        const apellido = pedido.usuarioInfo?.apellido || pedido.apellido || '';
        const nombreCompleto = `${nombre} ${apellido}`.trim();
        const sector = pedido.sectorInfo?.sector || pedido.usuarioInfo?.sectorinfo?.sector || pedido.sector || '';
        const ubicacion = pedido.sede?.nombre_sede || pedido.sede?.direccion || pedido.ubicacion || '';
        const plato = pedido.plato?.nombre || pedido.nombre || '';
        zpl += `^FO50,30^ADN,20,10^FDCIASA^FS\n^FO50,60^ADN,18,9^FD${nombreCompleto}^FS\n^FO50,85^ADN,14,7^FD${sector}^FS\n^FO50,105^ADN,14,7^FD${ubicacion}^FS\n^FO50,140^ADN,16,8^FD${plato}^FS\n^FO280,60^ADN,12,6^FDVENCE:^FS\n^FO280,85^ADN,16,8^FD${fechaVencimiento.toLocaleDateString('es-AR')}^FS\n^FO270,30^GB1,140,2^FS\n`;
    }

    zpl += `^XZ\n`;
    return zpl;
};

/**
 * Genera el ZPL crudo con UTF-8 para impresoras Honeywell/Zebra
 */
export const generateRawZPL = (pedidos, config) => {
    if (!pedidos || pedidos.length === 0) return '';

    let campos = config?.campos || [
        { id: 'header', label: 'CIASA', x: 10, y: 5, fontSize: 30, visible: true },
        { id: 'nombre', label: 'Nombre', x: 10, y: 15, fontSize: 20, visible: true, prefijo: '' },
        { id: 'sector', label: 'Sector', x: 10, y: 20, fontSize: 18, visible: true, prefijo: '' },
        { id: 'ubicacion', label: 'Ubicación', x: 10, y: 25, fontSize: 18, visible: true, prefijo: '' },
        { id: 'plato', label: 'Plato', x: 10, y: 30, fontSize: 16, visible: true, prefijo: '' },
        { id: 'fechaVencimiento', label: 'Vence', x: 10, y: 37, fontSize: 14, visible: true, prefijo: '' },
    ];

    const DPI = 203;
    const MM_TO_DOTS = DPI / 25.4;

    let zplCode = '';
    pedidos.forEach((pedido) => {
        zplCode += '^XA\n^LH0,0\n^CI28\n'; // CI28 para UTF-8

        campos.filter(campo => campo.visible).forEach(campo => {
            const xDots = Math.round(campo.x * MM_TO_DOTS) || 0;
            const yDots = Math.round(campo.y * MM_TO_DOTS) || 0;
            const size = (campo.fontSize * 2) || 20;

            let contenido = '';
            switch (campo.id) {
                case 'header': contenido = 'CIASA'; break;
                case 'nombre':
                    const nombre = pedido.usuarioInfo?.nombreuser || pedido.nombreuser || '';
                    const apellido = pedido.usuarioInfo?.apellido || pedido.apellido || '';
                    contenido = `${campo.prefijo || ''}${nombre} ${apellido}`.trim();
                    break;
                case 'apellido': contenido = `${campo.prefijo || ''}${pedido.usuarioInfo?.apellido || pedido.apellido || ''}`; break;
                case 'plato': contenido = `${campo.prefijo || ''}${pedido.plato?.nombre || pedido.nombre || ''}`; break;
                case 'sector': contenido = `${campo.prefijo || ''}${pedido.sectorInfo?.sector || pedido.usuarioInfo?.sectorinfo?.sector || pedido.sector || ''}`; break;
                case 'ubicacion': contenido = `${campo.prefijo || ''}${pedido.sede?.nombre_sede || pedido.sede?.direccion || pedido.ubicacion || ''}`; break;
                case 'sede': contenido = `${campo.prefijo || ''}${pedido.sede?.nombre_sede || pedido.sede?.direccion || pedido.ubicacion || ''}`; break;
                case 'fechaVencimiento':
                    const fv = new Date(new Date(pedido.fecha).getTime() + 7 * 24 * 60 * 60 * 1000);
                    contenido = `${campo.prefijo || ''}${fv.toLocaleDateString('es-AR')}`;
                    break;
                default: contenido = campo.label || '';
            }

            // Sanitizar texto para ZPL
            contenido = contenido.replace(/[^\x20-\x7E]/g, '?').substring(0, 50);

            if (campo.id === 'separador') {
                zplCode += `^FO${xDots},${yDots}^GB${Math.round(80 * MM_TO_DOTS)},3,3^FS\n`;
            } else if (campo.id === 'qrCode') {
                const qrMag = Math.round((campo.qrSize || 30) * MM_TO_DOTS / 20);
                const nombreUsuario = pedido.usuarioInfo?.nombreuser || pedido.nombreuser || '';
                zplCode += `^FO${xDots},${yDots}^BQN,2,${qrMag}^FDQA,${getPedidoId(pedido)}|${nombreUsuario}^FS\n`;
            } else if (campo.id === 'barcode') {
                zplCode += `^FO${xDots},${yDots}^BCN,${Math.round((campo.barcodeHeight || 50) * MM_TO_DOTS)},Y,N,N^FD${getPedidoId(pedido)}^FS\n`;
            } else if (contenido.trim()) {
                const rot = { 0: 'N', 90: 'R', 180: 'I', 270: 'B' }[campo.rotation || 0] || 'N';
                zplCode += `^CF0,${size}\n^FO${xDots},${yDots}^A0${rot},${size},${size}^FD${contenido}^FS\n`;
            }
        });
        zplCode += '^XZ\n';
    });
    return zplCode;
};

/**
 * Genera el HTML para el PDF de etiquetas
 */
export const generatePDFHTML = (pedidos, config) => {
    let campos = config?.campos || [
        { id: 'header', label: 'CIASA', x: 10, y: 5, fontSize: 30, visible: true },
        { id: 'nombre', label: 'Nombre', x: 10, y: 15, fontSize: 20, visible: true, prefijo: '' },
        { id: 'sector', label: 'Sector', x: 10, y: 20, fontSize: 18, visible: true, prefijo: '' },
        { id: 'ubicacion', label: 'Ubicación', x: 10, y: 25, fontSize: 18, visible: true, prefijo: '' },
        { id: 'plato', label: 'Plato', x: 10, y: 30, fontSize: 16, visible: true, prefijo: '' },
        { id: 'fechaVencimiento', label: 'Vence', x: 10, y: 37, fontSize: 14, visible: true, prefijo: '' },
    ];

    let items = '';
    pedidos.forEach(pedido => {
        // Log temporal para debugging
        console.log('📋 Pedido completo:', {
            id: pedido.id_pedido,
            sector_legacy: pedido.sector,
            sectorInfo: pedido.sectorInfo,
            id_sector: pedido.id_sector
        });

        items += '<div class="etiqueta">';
        campos.filter(c => c.visible).forEach(c => {
            const x = (c.x / 100) * 100;
            const y = (c.y / 50) * 100;
            let val = '';
            switch (c.id) {
                case 'header': val = 'CIASA'; break;
                case 'nombre':
                    // Combinar nombre y apellido
                    const nombre = pedido.usuarioInfo?.nombreuser || pedido.nombreuser || '';
                    const apellido = pedido.usuarioInfo?.apellido || pedido.apellido || '';
                    val = `${c.prefijo || ''}${nombre} ${apellido}`.trim();
                    break;
                case 'apellido':
                    // Mantener por compatibilidad con configs antiguas
                    val = `${c.prefijo || ''}${pedido.usuarioInfo?.apellido || pedido.apellido || ''}`;
                    break;
                case 'plato':
                    val = `${c.prefijo || ''}${pedido.plato?.nombre || pedido.nombre || ''}`;
                    break;
                case 'sector':
                    val = `${c.prefijo || ''}${pedido.sectorInfo?.sector || pedido.usuarioInfo?.sectorinfo?.sector || pedido.sector || ''}`;
                    break;
                case 'ubicacion':
                    val = `${c.prefijo || ''}${pedido.sede?.nombre_sede || pedido.sede?.direccion || pedido.ubicacion || ''}`;
                    break;
                case 'sede':
                    val = `${c.prefijo || ''}${pedido.sede?.nombre_sede || pedido.sede?.direccion || pedido.ubicacion || ''}`;
                    break;
                case 'fechaVencimiento':
                    const fv = new Date(new Date(pedido.fecha).getTime() + 7 * 24 * 60 * 60 * 1000);
                    val = `${c.prefijo || ''}${fv.toLocaleDateString('es-AR')}`;
                    break;
                default: val = c.label || '';
            }

            if (c.id === 'separador') {
                items += `<div class="separador" style="left:${x}%; top:${y}%; width:80%;"></div>`;
            } else if (c.id === 'qrCode' || c.id === 'barcode') {
                items += `<div class="placeholder" style="left:${x}%; top:${y}%; width:30%; height:20%; border:1px solid #000; font-size:10px;">${c.id.toUpperCase()}</div>`;
            } else {
                items += `<div class="campo" style="left:${x}%; top:${y}%; font-size:${c.fontSize * 1.2}px; transform:rotate(${c.rotation || 0}deg);">${val}</div>`;
            }
        });
        items += '</div>';
    });

    return `
        <html>
        <head>
            <style>
                body { font-family: sans-serif; margin: 0; background: white; }
                .page { display: flex; flex-wrap: wrap; gap: 5mm; padding: 10mm; }
                .etiqueta { width: 100mm; height: 50mm; border: 1px solid #000; position: relative; overflow: hidden; page-break-inside: avoid; }
                .campo { position: absolute; color: #000; white-space: nowrap; }
                .separador { position: absolute; background: #000; height: 2px; }
                .placeholder { position: absolute; display: flex; align-items: center; justify-content: center; background: #eee; }
                @media print { .page { padding: 0; } .etiqueta { border: 1px solid #000; } }
            </style>
        </head>
        <body onload="window.print()">
            <div class="page">${items}</div>
        </body>
        </html>
    `;
};
